import axios from 'axios';
import { orderBy, filterBy, process as processGridData, GroupDescriptor } from '@progress/kendo-data-query';
import { toasterAlert } from '../redux/toasterSlice';
import { store } from '../store';
import { PERMISSIONS, ROLES } from './Constant';
import { setAttendantUserSummary, setPriorityNotes, setSignOffStats } from '../redux/globalSlice';
import { getUserPriorityNotes } from '../services/UserService';
import { toArrayResponse } from '../views/inbox/utils/utils';

const getUserToken = () => {
   return localStorage.getItem('RPMAuthToken') ?? null
}

const logOut = () => {
  localStorage.removeItem('RPMAuthToken')
  window.location.reload();
}

const getAttendantUserSummary = async (cancelToken?: any) => {
  const roles = (store.getState().global.authUserAllData as { roles?: string[] })?.roles;
  if (!roles?.includes(ROLES.ATTENDANT)) {
    return;
  }
  return axios.get(`${process.env.VITE_API_URL}api/dashboard/top-metrics`, {
    cancelToken: cancelToken
  }).then((response: any) => {
    store.dispatch(setAttendantUserSummary(response));
  }).catch((e: any) =>
    store.dispatch(
      toasterAlert({
        type: 'error',
        title: 'Error',
        message: e?.response?.data?.hint || 'Failed to refresh attendant user summary.'
      })
    )
  );
}

export type AddressPostalLookupParams = {
  address?: string;
  city?: string;
  state?: string;
};

export type PostalCodeLookupResult = {
  postalCode: string | null;
  error?: string;
  hint?: string;
};

const getPostalCodeLookupMessage = (result: PostalCodeLookupResult): string => {
  // const message = [result.hint, result.error].filter(Boolean).join(' - ');
  return result.error || "Couldn't lookup postal code";
};

const getPostalCodeFromAddress = async ({
  address,
  city,
  state
}: AddressPostalLookupParams, cancelToken?: any): Promise<PostalCodeLookupResult> => {
  const trimmedAddress = address?.trim();
  const trimmedCity = city?.trim();
  const trimmedState = state?.trim();

  if (!trimmedAddress || !trimmedCity || !trimmedState) {
    return { postalCode: null };
  }

  try {
    const response: { postalCode?: string } = await axios.post(
      `${process.env.VITE_API_URL}api/address/postal-code`,
      {
        address: trimmedAddress,
        city: trimmedCity,
        state: trimmedState.toUpperCase()
      },
      { cancelToken }
    );

    const postalCode = response?.postalCode?.trim() || null;
    if (!postalCode) {
      return {
        postalCode: null,
        error: 'No postal code found for address, please copy the address and check manually.',
        hint: "Couldn't lookup postal code."
      };
    }

    return { postalCode };
  } catch (error: any) {
    return {
      postalCode: null,
      error: error?.response?.data?.error,
      hint: error?.response?.data?.hint
    };
  }
};

export type PostalCodeBulkSyncParams = {
  patientIds: number[];
};

export type PostalCodeBulkSyncResult = {
  status: 'success' | 'failed';
};

const syncPostalCodeBulk = async (
  { patientIds }: PostalCodeBulkSyncParams,
  cancelToken?: any
): Promise<boolean> => {
  const uniquePatientIds = Array.from(
    new Set(patientIds.filter((id) => Number.isInteger(id) && id > 0))
  );

  if (uniquePatientIds.length === 0) {
    return false;
  }

  const response = await axios.post(
    `${process.env.VITE_API_URL}api/address/postal-code-bulk`,
    { patientIds: uniquePatientIds },
    { cancelToken }
  );

  if (response) {
    store.dispatch(toasterAlert({
      type: 'success',
      title: 'Success',
      message: response || 'Zip codes synced successfully'
    }));
  }

  return true;
};

const getUnsignedSignOffStats = async (cancelToken?: any) => {
  return axios.get(`${process.env.VITE_API_URL}api/unsigned-stats`, {
    cancelToken
  }).then((stats: any) => {
    const periods = stats?.periods ?? 0;
    const carePlans = stats?.carePlans ?? 0;
    const rtmPeriods = stats?.rtmPeriods ?? 0;
    const prepareCarePlans = stats?.prepareCarePlans ?? 0;
    store.dispatch(setSignOffStats({
      periods,
      carePlans,
      rtmPeriods,
      prepareCarePlans,
      total: periods + carePlans + rtmPeriods
    }));
  }).catch((e: any) =>
    store.dispatch(
      toasterAlert({
        type: 'error',
        title: 'Error',
        message: e?.response?.data?.hint || 'Failed to refresh unsigned sign-off stats.'
      })
    )
  );
};

const getPriorityNotes = async (cancelToken?: any) => {
  return getUserPriorityNotes(cancelToken).then((response: any) => {
    store.dispatch(setPriorityNotes(response?.data ?? response));
  }).catch((e: any) => {
    if (axios.isCancel(e)) {
      return;
    }
    store.dispatch(setPriorityNotes([]));
    store.dispatch(
      toasterAlert({
        type: 'error',
        title: 'Something went wrong!',
        message: e?.response?.data?.hint ??
          e?.response?.data?.message ??
          'Failed to fetch priority notes'
      })
    );
  });
};

//** Kendo grid functions START */

const normalizeDateFilters = (inputFilter: any, dateFields: string[]): any => {
  const dateFieldSet = new Set(dateFields);

  const toDateOrNull = (val: any): Date | null => {
    if (val == null || val === '') return null;
    if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  // Use local day boundaries so date-equals covers the selected local day.
  const startOfDayLocal = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const nextDayLocal = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);

  const transform = (f: any): any => {
    if (!f) return f;
    if (f.filters && Array.isArray(f.filters)) {
      return {
        ...f,
        filters: f.filters.map(transform)
      };
    }

    if (f.field && dateFieldSet.has(f.field)) {
      const valueAsDate = toDateOrNull(f.value);
      if (!valueAsDate) {
        return { ...f, value: null };
      }

      if (f.operator === 'eq') {
        const start = startOfDayLocal(valueAsDate);
        const end = nextDayLocal(valueAsDate);
        return {
          logic: 'and',
          filters: [
            { field: f.field, operator: 'gte', value: start },
            { field: f.field, operator: 'lt', value: end }
          ]
        };
      }

      return { ...f, value: valueAsDate };
    }

    return f;
  };

  return transform(inputFilter);
}

/** Billing grids: normalize date filters, then Kendo process (sort/filter/group/page). */
const processWithGroups = (
  data: any[],
  group: GroupDescriptor[],
  filter: any,
  sort: any,
  skip: number,
  take: number,
  dateFields: string[]
): { processedData: any[]; totalRecords: number } => {
  const normalizedFilter = filter
    ? normalizeDateFilters(filter, dateFields)
    : filter;

  const result = processGridData(data, {
    sort,
    filter: normalizedFilter,
    group,
    skip,
    take
  });

  return { processedData: result.data, totalRecords: result.total };
};

/** Recursively flatten Kendo grouped grid data to leaf rows for Excel export. */
const flattenGroupedGridData = (data: any[]): any[] => {
  if (!Array.isArray(data)) return [];
  return data.flatMap((item) => {
    if (item?.field != null && Array.isArray(item.items)) {
      return flattenGroupedGridData(item.items);
    }
    return [item];
  });
};

/** Kendo grid: sort + filter + page (no grouping). Use `totalRecords` for server-style pagination total. */
const processData = (
  data: any[],
  filter: any,
  sort: any,
  skip: number,
  take: number
): { processedData: any[]; totalRecords: number } => {
  let newData = [...data];

  if (sort && sort.length > 0) {
    newData = orderBy(newData, sort);
  }

  if (filter) {
    newData = filterBy(newData, filter);
  }

  const totalRecords = newData.length;
  const processedData = newData.slice(skip, skip + take);
  return { processedData, totalRecords };
};

//** Kendo grid functions END */

const formatPhoneValue = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatPostalCodeInput = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const formatFullAddress = (...parts: Array<string | undefined | null>): string =>
  parts.map((part) => String(part || '').trim()).filter(Boolean).join(', ');

type CopyToClipboardOptions = {
  successMessage?: string;
  emptyMessage?: string;
};

const copyToClipboard = async (
  value: string,
  options: CopyToClipboardOptions = {}
): Promise<void> => {
  const text = String(value || '').trim();
  const {
    successMessage = 'Copied to clipboard',
    emptyMessage = 'Nothing to copy'
  } = options;

  if (!text) {
    store.dispatch(toasterAlert({
      type: 'error',
      title: 'Error',
      message: emptyMessage
    }));
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
    store.dispatch(toasterAlert({
      type: 'success',
      title: 'Copied',
      message: successMessage
    }));
  } catch {
    store.dispatch(toasterAlert({
      type: 'error',
      title: 'Error',
      message: 'Failed to copy to clipboard'
    }));
  }
};

const hasEncounterSignAccess = (): boolean => {
  const userAllData: any = store.getState().global.authPermissions;
  return userAllData?.includes(PERMISSIONS.ENCOUNTERSIGNMODULE);
}

export {
  getUserToken,
  logOut,
  getAttendantUserSummary,
  getPostalCodeFromAddress,
  getPostalCodeLookupMessage,
  syncPostalCodeBulk,
  getUnsignedSignOffStats,
  getPriorityNotes,
  normalizeDateFilters,
  processWithGroups,
  flattenGroupedGridData,
  processData,
  formatPhoneValue,
  formatPostalCodeInput,
  formatFullAddress,
  copyToClipboard,
  hasEncounterSignAccess
}
